import { useState, useEffect } from "react";
import useUser from "@hooks/use_user";

export const useTheme = () => {
    const [theme, setTheme] = useState("system");
    const [mounted, setMounted] = useState(false);
    const [systemTheme, setSystemTheme] = useState("light");
    const user = useUser();

    const getSystemTheme = (theme) => {
        if (typeof window === "undefined") return "light";
        if (theme === "system") {
            return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        }
        return theme;
    };

    useEffect(() => {
        setMounted(true);
        // Sækja vistuð þemu stillingar ef þær eru til
        try {
            const savedTheme = localStorage.getItem("theme");
            if (savedTheme) {
                // Ef notandi er ekki innskráður og þemað er "system", setjum það í "light"
                const themeToSet = !user && savedTheme === "system" ? "light" : savedTheme;
                setTheme(themeToSet);
                const newSystemTheme = getSystemTheme(themeToSet);
                setSystemTheme(newSystemTheme);
                updateTheme(themeToSet);
            } else {
                // Nota light stillingar fyrir óinnskráða notendur, system fyrir innskráða
                const defaultTheme = user ? "system" : "light";
                setTheme(defaultTheme);
                const newSystemTheme = getSystemTheme(defaultTheme);
                setSystemTheme(newSystemTheme);
                updateTheme(defaultTheme);
            }
        } catch (error) {
            console.error("Villa við að sækja þemu", error);
        }
    }, [user]);

    // Uppfæra þema þegar notandi breytist
    useEffect(() => {
        if (!user) {
            setThemeDirectly("light");
        }
    }, [user]);

    const updateTheme = (newTheme) => {
        const newSystemTheme = getSystemTheme(newTheme);
        setSystemTheme(newSystemTheme);
        document.documentElement.setAttribute("data-theme", newSystemTheme);
    };

    const setThemeDirectly = (newTheme) => {
        try {
            setTheme(newTheme);
            updateTheme(newTheme);
            localStorage.setItem("theme", newTheme);
        } catch (error) {
            console.error("Villa við að setja þema", error);
        }
    };

    const toggleTheme = () => {
        try {
            // Einföld umskipti á milli ljóss og dökks þema
            const newTheme = theme === "dark" ? "light" : "dark";
            setThemeDirectly(newTheme);
        } catch (error) {
            console.error("Villa við að breyta þemu", error);
        }
    };

    // Hlusta á breytingar á kerfis þemu
    useEffect(() => {
        if (typeof window === "undefined") return;

        const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
        if (!mediaQuery) return;

        const handleChange = (e) => {
            const newSystemTheme = e.matches ? "dark" : "light";
            setSystemTheme(newSystemTheme);
            if (theme === "system") {
                document.documentElement.setAttribute("data-theme", newSystemTheme);
            }
        };

        // Setja uppfæra systemTheme strax
        const initialTheme = mediaQuery.matches ? "dark" : "light";
        setSystemTheme(initialTheme);

        try {
            mediaQuery.addEventListener("change", handleChange);
            return () => mediaQuery.removeEventListener("change", handleChange);
        } catch (error) {
            console.error("Villa við að setja upp event listener fyrir þemu", error);
        }
    }, [theme]);

    return { theme, systemTheme, toggleTheme, setThemeDirectly, mounted };
};
